import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ENGLISH_TEST_TYPES, QUALIFICATIONS } from 'apply-data';
import { IOffer } from 'api-paths-cas';
import { InputWithConfirm, RadioOptionsWithConfirm, SelectWithOtherWithConfirm } from 'shared-components';

import { Q } from '../../../../store/student/types';
import { InputFieldConfig, IImportData } from '../../../../types';
import { savePI } from '../../../../store/student/actions';
import { createUpdatePIFieldAction } from '../../../../store/student/personalInfo/actions';

import QuestionPage from '../../../templates/question';
import { getImportOffer } from '../../../../utilities/helpers';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { Pages } from '../../../../store/student/personalInfo/types';

export interface IProps {
  sectionTitle: string;
  complete: boolean;
  currentStep: number;
  steps: number;
  hasEnglishTest?: boolean;
  hasEnglishTestVisible: boolean;
  hasEnglishTestConfig: InputFieldConfig;
  englishTestType?: string;
  englishTestTypeVisible: boolean;
  englishTestTypeConfig: InputFieldConfig;
  englishTestTypeOther?: string;
  englishTestTypeOtherVisible: boolean;
  englishTestTypeOtherConfig: InputFieldConfig;
  englishTestNotReqdReasonConfig: InputFieldConfig;
  englishTestNotReqdReasonOtherConfig: InputFieldConfig;
  englishTestNotReqdReasonVisible: boolean;
  englishTestNotReqdReason?: string;
  englishTestNotReqdReasonOther?: string;
  qualificationConfig: InputFieldConfig;
  qualificationOtherConfig: InputFieldConfig;
  qualification?: string;
  qualificationOther?: string;
  qualificationVisible?: boolean;
  qualificationOtherVisible?: boolean;
  config: any;
  isPageComplete?: boolean;
  importData: IImportData;
  offer: IOffer;
  isStudentFromEnglishSpeakingCountry?: boolean;
}

type Props = IProps;

interface IState {
  hasEnglishTest?: boolean;
  englishTestType?: string;
  otherEnglishTestType?: string;
  importApproved: string[];
  importUpdateEnabled: string[];
  importEdited: string[];
}

const charsValueMap: { [key: string]: string } = {
  '1': 'English speaking country',
  '2': 'Previous test',
  '3': 'Previous visa application',
  '4': 'US study abroad',
  '5': 'Gifted student',
  '6': 'Other',
};

const PersonalQPage6: React.FC<Props> = props => {
  const dispatch = useDispatch();
  // const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE6, config));
  };
  const onChange = (fieldName: string, value: any) => dispatch(createUpdatePIFieldAction(fieldName, value));

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();
  // });

  const getImportApprovedFields = (): Array<Q> => {
    const importOffer = getImportOffer(props.importData, props.offer.intakeId, props.offer.offerId);
    const importApproved = [];
    if (importOffer?.isSELTReqd !== undefined && importOffer?.isSELTReqd === props.hasEnglishTest) {
      importApproved.push(Q.HAS_ENGLISH_TEST);
    }
    if (props.isPageComplete) {
      if (props.importData?.englishTestProvider === props.englishTestType && props.hasEnglishTest) {
        importApproved.push(Q.ENGLISH_TEST_TYPE);
      }
    }
    return importApproved;
  };

  const getImportUpdateFields = (): Array<Q> => {
    const importOffer = getImportOffer(props.importData, props.offer.intakeId, props.offer.offerId);
    const importUpdateEnabled = [];
    if (props.isPageComplete) {
      if (importOffer?.isSELTReqd !== undefined && props.hasEnglishTest !== importOffer?.isSELTReqd) {
        importUpdateEnabled.push(Q.HAS_ENGLISH_TEST);
      }
      if (
        props.importData.englishTestProvider &&
        props.englishTestType !== props.importData.englishTestProvider &&
        props.hasEnglishTest
      ) {
        importUpdateEnabled.push(Q.ENGLISH_TEST_TYPE);
      }
    }
    return importUpdateEnabled;
  };

  const [state, setState] = useState<IState>({
    hasEnglishTest: props.hasEnglishTest,
    englishTestType: props.englishTestType,
    otherEnglishTestType: props.englishTestType,
    importApproved: getImportApprovedFields(),
    importUpdateEnabled: getImportUpdateFields(),
    importEdited: [],
  });

  useEffect(() => {
    const importOffer = getImportOffer(props.importData, props.offer.intakeId, props.offer.offerId);
    if (importOffer?.isSELTReqd && props.hasEnglishTest === undefined) {
      onChange(Q.HAS_ENGLISH_TEST, importOffer?.isSELTReqd);
      setState(prevState => ({
        ...prevState,
        importApproved: [...prevState.importApproved, Q.HAS_ENGLISH_TEST],
      }));
    }
  }, [props.importData, props.offer.intakeId, props.offer.offerId, props.hasEnglishTest, onChange]);

  const handleYesNoClick = (target: Q, question: any): void => {
    onChange(target, question.char === 'Y');
    if (question.char === 'Y' && target === Q.HAS_ENGLISH_TEST) {
      setState(prevState => ({
        ...prevState,
        englishTestType: isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? state.englishTestType : '',
        importApproved: getImportApprovedFields(),
      }));
      onChange(Q.ENGLISH_TEST_TYPE, isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? state.englishTestType : '');
      onChange(Q.ENGLISH_TEST_NOT_REQD_REASON, '');
      onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, '');
      onChange(Q.QUALIFICATION, '');
      onChange(Q.QUALIFICATION_OTHER, '');
    }
  };

  const isImportEnabled = (target: Q): boolean => {
    const importOffer = getImportOffer(props.importData, props.offer.intakeId, props.offer.offerId);
    switch (target) {
      case Q.HAS_ENGLISH_TEST:
        return props.hasEnglishTestVisible && importOffer?.isSELTReqd !== undefined;
      case Q.ENGLISH_TEST_TYPE:
        return props.englishTestTypeVisible && !!props.importData.englishTestProvider;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = (target: Q): boolean => {
    if (!props.isPageComplete || !props.complete) return false;
    switch (target) {
      case Q.HAS_ENGLISH_TEST:
        return props.hasEnglishTest !== undefined && state.importUpdateEnabled.includes(target);
      case Q.ENGLISH_TEST_TYPE:
        return !!props.englishTestType && state.importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const handleImportApprove = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: [...prevState.importApproved, target],
      importEdited: prevState.importEdited.filter(q => q !== target),
    }));
  };

  const handleImportEdit = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: prevState.importApproved.filter(q => q !== target),
      importEdited: [...prevState.importEdited, target],
    }));
  };

  const handleImportUndo = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: prevState.importApproved.filter(q => q !== target),
      importEdited: prevState.importEdited.filter(q => q !== target),
    }));
  };

  const handleQualificationOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Q.QUALIFICATION, e.currentTarget.value);
    if (props.qualificationOther) {
      onChange(Q.QUALIFICATION_OTHER, '');
    }
  };

  const notReqdReasons = [
    {
      label: 'My English was assessed based on my previous qualification',
      selected: props.englishTestNotReqdReason === charsValueMap['2'],
    },
    {
      label: 'I have met the English language requirement in my previous visa application',
      selected: props.englishTestNotReqdReason === charsValueMap['3'],
    },
    {
      label: "I'm coming as a part of US study abroad programme",
      selected: props.englishTestNotReqdReason === charsValueMap['4'],
    },
    {
      label: "I'm a gifted student",
      selected: props.englishTestNotReqdReason === charsValueMap['5'],
    },
    {
      label: "Other/I don't know",
      selected: props.englishTestNotReqdReason === charsValueMap['6'],
    },
  ];

  if (props.isStudentFromEnglishSpeakingCountry)
    notReqdReasons.unshift({
      label: 'I am a national of a majority English-speaking country',
      selected: props.englishTestNotReqdReason === charsValueMap['1'],
    });

  const importOffer = getImportOffer(props.importData, props.offer.intakeId, props.offer.offerId);

  return (
    <QuestionPage
      complete={props.complete}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={() => onNext(props.config)}
      onBackToTaskList={onBackToTaskList}
      section={props.sectionTitle}
    >
      {props.hasEnglishTestVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_ENGLISH_TEST}
          id={Q.HAS_ENGLISH_TEST}
          value={props.hasEnglishTest ? 'Yes' : 'No'}
          label={props.hasEnglishTestConfig?.label}
          inputHelperText={props.hasEnglishTestConfig?.helpText}
          onClick={(question: any) => handleYesNoClick(Q.HAS_ENGLISH_TEST, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: props.hasEnglishTest === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: props.hasEnglishTest === false,
            },
          ]}
          importEnabled={isImportEnabled(Q.HAS_ENGLISH_TEST)}
          importValue={importOffer?.isSELTReqd ? 'Yes' : 'No'}
          importUpdateEnabled={isImportUpdateEnabled(Q.HAS_ENGLISH_TEST)}
          importApproved={state.importApproved.includes(Q.HAS_ENGLISH_TEST)}
          importEdited={state.importEdited.includes(Q.HAS_ENGLISH_TEST)}
          onImportApprove={() => {
            handleImportApprove(Q.HAS_ENGLISH_TEST);
            onChange(Q.HAS_ENGLISH_TEST, importOffer?.isSELTReqd);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.HAS_ENGLISH_TEST);
            onChange(
              Q.HAS_ENGLISH_TEST,
              isImportUpdateEnabled(Q.HAS_ENGLISH_TEST) ? state.hasEnglishTest : importOffer?.isSELTReqd,
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.HAS_ENGLISH_TEST);
            onChange(Q.HAS_ENGLISH_TEST, isImportUpdateEnabled(Q.HAS_ENGLISH_TEST) ? state.hasEnglishTest : '');
          }}
        />
      )}

      {props.englishTestTypeVisible && (
        <SelectWithOtherWithConfirm
          key={Q.ENGLISH_TEST_TYPE}
          id={Q.ENGLISH_TEST_TYPE}
          label={props.englishTestTypeConfig?.label}
          options={ENGLISH_TEST_TYPES}
          otherOptions={[
            {
              label: 'Other',
              value: 'Other',
            },
          ]}
          otherOptionLabel={props.englishTestTypeOtherConfig?.label}
          value={props.englishTestType}
          onChange={(evt: any) => onChange(Q.ENGLISH_TEST_TYPE, evt.currentTarget.value)}
          onChangeOtherText={(evt: any) => onChange(Q.ENGLISH_TEST_TYPE_OTHER, evt.currentTarget.value)}
          inputHelperText={props.englishTestTypeConfig?.helpText}
          otherValue={props.englishTestTypeOther}
          disableOther={false}
          importEnabled={isImportEnabled(Q.ENGLISH_TEST_TYPE)}
          importValue={props.importData.englishTestProvider}
          importUpdateEnabled={isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE)}
          importApproved={state.importApproved.includes(Q.ENGLISH_TEST_TYPE)}
          importEdited={state.importEdited.includes(Q.ENGLISH_TEST_TYPE)}
          onImportApprove={() => {
            handleImportApprove(Q.ENGLISH_TEST_TYPE);
            onChange(Q.ENGLISH_TEST_TYPE, props.importData.englishTestProvider);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.ENGLISH_TEST_TYPE);
            onChange(
              Q.ENGLISH_TEST_TYPE,
              isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? props.englishTestType : props.importData.englishTestProvider,
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.ENGLISH_TEST_TYPE);
            onChange(Q.ENGLISH_TEST_TYPE, isImportUpdateEnabled(Q.ENGLISH_TEST_TYPE) ? state.englishTestType : '');
          }}
        />
      )}

      {props.englishTestNotReqdReasonVisible && (
        <RadioOptionsWithConfirm
          key={Q.ENGLISH_TEST_NOT_REQD_REASON}
          id={Q.ENGLISH_TEST_NOT_REQD_REASON}
          label={props.englishTestNotReqdReasonConfig?.label}
          inputHelperText={props.englishTestNotReqdReasonConfig?.helpText}
          onClick={(question: any) => {
            const char = !props.isStudentFromEnglishSpeakingCountry ? question.char + 1 : question.char;
            const answer = charsValueMap[char];
            onChange(Q.ENGLISH_TEST_NOT_REQD_REASON, answer);
            if (props.englishTestNotReqdReasonOther) {
              onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, '');
            }
            if (props.qualification) {
              onChange(Q.QUALIFICATION, '');
            }
            if (props.qualificationOther) {
              onChange(Q.QUALIFICATION_OTHER, '');
            }
          }}
          questions={notReqdReasons.map((reason, i) => ({ char: i + 1, ...reason }))}
        />
      )}

      {props.englishTestNotReqdReason === charsValueMap[6] && !props.hasEnglishTest ? (
        <InputWithConfirm
          id={`${Q.ENGLISH_TEST_NOT_REQD_REASON}_other`}
          label={props.englishTestNotReqdReasonOtherConfig.label}
          onChange={(evt: any) => onChange(Q.ENGLISH_TEST_NOT_REQD_REASON_OTHER, evt.currentTarget.value)}
          value={props.englishTestNotReqdReasonOther}
        />
      ) : null}

      {props.qualificationVisible && (
        <SelectWithOtherWithConfirm
          key={Q.QUALIFICATION}
          id={Q.QUALIFICATION}
          label={props.qualificationConfig?.label}
          options={QUALIFICATIONS}
          otherOptions={[
            {
              label: 'Other',
              value: 'Other',
            },
          ]}
          otherOptionLabel={props.qualificationOtherConfig?.label}
          value={props.qualification}
          onChange={handleQualificationOnChange}
          onChangeOtherText={(evt: any) => onChange(Q.QUALIFICATION_OTHER, evt.currentTarget.value)}
          inputHelperText={props.qualificationConfig?.helpText}
          otherValue={props.qualificationOther}
          disableOther={false}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage6;
